/**
 * The postMessage contract between the merchant's page and our iframe.
 *
 * This file is the ONLY definition of that contract. The SDK (which runs on
 * the merchant's page) and the embed app (which runs in our iframe) both
 * import it, so a renamed field breaks the build instead of silently breaking
 * carts in production.
 *
 * Shape: the host sends a `Command` with a unique `id`; the bridge answers
 * with a `Reply` carrying the same `id`. The bridge may also push unsolicited
 * `EventMessage`s (e.g. the cart changed in another tab).
 */

/** Every message we send carries this, so we never act on a stray postMessage. */
export const PROTOCOL_NAMESPACE = "storedum" as const;

/** Bumped only on a breaking change to the shapes below. */
export const PROTOCOL_VERSION = 1 as const;

export type CommandType =
    | "bridge.init"
    | "cart.add"
    | "cart.updateQuantity"
    | "cart.remove"
    | "cart.summary"
    | "account.logout"
    /**
     * The host signed somebody in WITHOUT the panel, and hands us the proof.
     *
     * Its own flows — an abandoned-cart link, an order-tracking link, an OTP
     * answered inside the chat widget — already know the shopper's number, so
     * they verify a token-scoped OTP against the tenant and get a tenant
     * session out of it. The identity host never hears about any of that, so
     * the shopper stays signed in on that one store and the next one asks who
     * they are all over again.
     *
     * The host mints a reverse handoff on the tenant (`POST
     * /Users/SsoMintReverseHandoff`) and sends it here. We redeem it from
     * INSIDE the frame, which is the whole point: `/Sso/AcceptReverseHandoff`
     * sets `sso_sid`, and that cookie is only first-party here.
     */
    | "account.adoptSession"
    /**
     * The host's session token changed since `bridge.init`.
     *
     * It is captured once at init and kept for the handoff, which is right
     * until the host's own session changes without a page load — a shopper
     * signing out and someone else signing in on the same SPA navigation. The
     * handoff would then merge the FIRST session's cart into the SECOND
     * account, because the server resolves this token to a user and moves
     * their cart without checking that it was a guest.
     *
     * So the host sends the current one before it opens a sign-in, and clears
     * it on logout.
     */
    | "bridge.setHostToken"
    /**
     * The host's GUEST CART TOKEN changed since `bridge.init`.
     *
     * Token-cart stores (cart.guest_token_mode) name a guest's cart with an
     * opaque token instead of minting a user. The storefront and this frame
     * each hold a copy; this keeps the frame's current before a sign-in, for
     * the same reason `bridge.setHostToken` exists for the bearer.
     */
    | "bridge.setHostCartToken"
    /**
     * When would an order to this pincode arrive?
     *
     * Answered by the tenant's own API, which knows its couriers and its
     * cut-offs. Asked through the bridge rather than by whoever needs it,
     * because the bridge is the one place holding this store's API base and
     * the shopper's token — and because the pincode it resolves is worth
     * keeping (see `delivery.pincode`).
     */
    | "delivery.estimate"
    /**
     * The pincode this shopper has told us, once, for every Storedum store.
     *
     * Kept HERE rather than on each storefront on purpose: it is the same fact
     * about the same person, and asking again on the next store is the kind of
     * friction the shared identity exists to remove. A storefront reads it
     * through the SDK and may mirror it into its own storage for a synchronous
     * first paint; this is the copy that outlives any one store.
     */
    | "delivery.pincode"
    /**
     * A Shopify storefront's cart changed (SH5).
     *
     * On Shopify the cart of record is Shopify's own, driven from the host
     * page over its AJAX API. The SDK's Shopify driver reports every mutation
     * it made or observed — with Shopify's own cart payload — and the bridge
     * mirrors it into the store's cart tables, so abandoned-cart automations,
     * Orbit and the funnel reports see carts that never touched Storedum's
     * cart API.
     */
    | "shopify.cartEvent"
    /** A Shopify product page was opened: the store's view log and the shopper's recently viewed trail. */
    | "shopify.productView"
    /** A product page on a host that logs its own views: only the recently viewed trail. */
    | "track.productView"
    /**
     * A storefront session began on a Shopify page — the storefront's own
     * session counter, ported: one `site_sessions` row per visit, so sessions,
     * bounce, duration and the funnel read one table for both channels.
     */
    | "session.start"
    /** The session's heartbeat: page count, exit page and duration so far. */
    | "session.update"
    /** The store's published buy dock and one product's data (`/BuyDock/Product`), for the dock frames. */
    | "buydock.load";

/** One line of Shopify's `/cart.js`, as far as the mirror and drawer read it. */
export interface ShopifyCartItem {
    /** The variant id (Shopify's `id` on a cart line IS the variant id). */
    id: number;
    /** Line key — `<variant id>:<hash>`; the handle `/cart/change.js` wants. */
    key?: string;
    variant_id?: number;
    product_id?: number;
    quantity: number;
    title?: string;
    product_title?: string;
    variant_title?: string | null;
    /** Money fields are in the currency's minor unit (paise). */
    price?: number;
    original_price?: number;
    final_price?: number;
    line_price?: number;
    original_line_price?: number;
    final_line_price?: number;
    image?: string | null;
    url?: string;
    handle?: string;
    sku?: string | null;
    properties?: Record<string, unknown> | null;
    product_has_only_default_variant?: boolean;
}

export interface ShopifyCart {
    token: string;
    item_count: number;
    /** Minor units, after every discount Shopify applied. */
    total_price: number;
    items_subtotal_price?: number;
    original_total_price?: number;
    total_discount?: number;
    currency?: string;
    note?: string | null;
    attributes?: Record<string, string>;
    items: ShopifyCartItem[];
}

export type ShopifyCartEventKind = "add" | "change" | "remove" | "sync" | "open" | "checkout";

export interface ShopifyCartEventInput {
    event: ShopifyCartEventKind;
    cart: ShopifyCart;
    /** The line an `add` added — what the add-to-cart event records. */
    changed?: { variant_id: number; quantity: number };
    /** Shopify's customer id when the shopper is signed in to the shop. */
    customer_id?: string | number | null;
    shop?: string;
}

export interface AddToCartInput {
    /**
     * Storedum product id. On Shopify the merchant page knows only a Shopify
     * variant id, so the SDK resolves it through the mapping first — see
     * `resolveShopifyVariant`. One of `product_id` or `shopify_variant_id`
     * must be present.
     */
    product_id?: number;
    variant_id?: number | null;
    shopify_variant_id?: string;
    quantity: number;
    /** Bundle-box products require the pre-built selection. */
    bundle_selection_id?: string | null;
}

export interface UpdateQuantityInput {
    product_id?: number;
    variant_id?: number | null;
    quantity: number;
    bundle_selection_id?: string | null;
    /** On a Shopify storefront the line is named by its Shopify variant id. */
    shopify_variant_id?: string | number;
}

export interface RemoveFromCartInput {
    product_id?: number;
    variant_id?: number | null;
    bundle_selection_id?: string | null;
    shopify_variant_id?: string | number;
}

/**
 * Attribution captured on the MERCHANT's page and forwarded on every command.
 *
 * It has to travel with each call, not just at checkout: `/Cart/Add` itself
 * writes AddToCartEvent and fires the Meta conversion trigger, so a cart add
 * without these loses its match keys and breaks the funnel at step one. The
 * cookies live on the merchant's origin, so only the SDK can read them.
 */
export interface AttributionContext {
    session_uuid?: string;
    _fbp?: string;
    _fbc?: string;
}

/**
 * A visit starting on the merchant's page — the body of the storefront's
 * `/SiteSessions/Record/Start`, field for field.
 */
export interface SessionStartInput {
    session_uuid: string;
    visitor_id?: string;
    landing_page?: string;
    referrer?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
    device_type?: string;
    browser?: string;
    os?: string;
    screen_width?: number;
    screen_height?: number;
}

/** A heartbeat — the body of `/SiteSessions/Record/Update`. */
export interface SessionUpdateInput {
    session_uuid: string;
    page_count?: number;
    exit_page?: string;
    duration_seconds?: number;
}

export interface LogoutInput {
    /**
     * `false` (default) — sign out of THIS store only. The shopper stays
     * recognised on other Storedum stores.
     * `true` — revoke the shared identity session too, signing them out
     * everywhere on this device.
     */
    everywhere?: boolean;
}

export interface BridgeInitInput {
    /** The merchant page's own origin, re-asserted for postMessage targeting. */
    origin: string;
    attribution?: AttributionContext;

    /**
     * The host's own session token for this store, when it has one.
     *
     * Storefronts hand guests a token before anybody signs in, and a cart
     * hangs off it. Passed to the handoff as `previous_auth_token` so the
     * server folds that guest's work into the account being signed into,
     * rather than leaving a shopper staring at an empty basket they filled a
     * minute ago.
     */
    previous_auth_token?: string;

    /**
     * The host's guest CART token (token-cart stores). Same journey as the
     * bearer above: it rides the sign-in handoff as `previous_cart_token`
     * so the guest's cart merges into the account.
     */
    previous_cart_token?: string;
}

/**
 * What the store can promise for one pincode.
 *
 * `date` is the API's own ISO date and is the only field anyone should format
 * from — `day` is a rendering of it ("Tomorrow", "Thu") computed where the
 * shopper's timezone is known, and is offered so three callers do not each
 * write that logic differently.
 *
 * `serviceable: false` with no date is a real answer, not a failure: the store
 * does not deliver there. A failure is `null` from the command.
 */
export interface DeliveryEstimate {
    pincode: string;
    serviceable: boolean;
    /** ISO `YYYY-MM-DD`, straight from the store's API. */
    date?: string;
    /** "Today" | "Tomorrow" | a weekday | a date, ready to render. */
    day?: string;
    cod_enabled?: boolean;
    returns_days?: number | null;
    replacements_days?: number | null;
    /** Why, when the store could not answer. */
    message?: string;
}

export interface CartSummary {
    total_items: number;
    total_price: number | null;
}

export interface CommandPayloads {
    "bridge.init": BridgeInitInput;
    "cart.add": AddToCartInput & { attribution?: AttributionContext };
    "cart.updateQuantity": UpdateQuantityInput & { attribution?: AttributionContext };
    "cart.remove": RemoveFromCartInput & { attribution?: AttributionContext };
    "cart.summary": { attribution?: AttributionContext };
    "account.logout": LogoutInput & { attribution?: AttributionContext };
    "account.adoptSession": { handoff: string; auth_token?: string };
    "bridge.setHostToken": { previous_auth_token?: string };
    "bridge.setHostCartToken": { previous_cart_token?: string };
    "delivery.estimate": { pincode?: string };
    "delivery.pincode": { set?: string | null };
    "shopify.cartEvent": ShopifyCartEventInput & { attribution?: AttributionContext };
    "shopify.productView": {
        shopify_product_id: string;
        shopify_variant_id?: string | null;
        attribution?: AttributionContext;
    };
    "track.productView": { product_id: number; name?: string; image?: string; price?: number | string };
    "session.start": SessionStartInput;
    "session.update": SessionUpdateInput;
    "buydock.load": BuyDockLoadInput;
}

/** Storedum ids on our storefront, Shopify ids on a Shopify theme. */
export interface BuyDockLoadInput {
    product_id?: number;
    variant_id?: number | null;
    shopify_product_id?: string;
    shopify_variant_id?: string | null;
}

/** The buy-more-save-more tier picked in the dock, which the bars and Add follow. */
export interface BuyDockTierPick {
    qty: number;
    total: number;
    save: number;
}

/**
 * What the page knows that a buy dock frame cannot: the cart, the variant on
 * screen, the viewport and the shopper's pincode. The SDK sends it with the
 * store's document and data as `buydock.state`, and again on every change.
 */
export interface BuyDockPageState {
    platform: "shopify" | "storedum";
    device: "mobile" | "desktop";
    /** Units of the variant on screen in the cart. */
    quantity: number;
    /** The whole cart, in rupees. */
    cartTotal: number;
    outOfStock: boolean;
    /** The page's own price for the variant on screen (Shopify's), which wins over the store's. */
    price: number | null;
    mrp: number | null;
    /** Desktop bar: the dock's action row has scrolled past. */
    shown: boolean;
    /** The on-screen keyboard is up (the mobile bar steps aside). */
    keyboard: boolean;
    /** The shopper's saved pincode, and the store's last answer for it. */
    pincode: string | null;
    delivery: { pincode: string; serviceable: boolean; date: string | null } | null;
    tierPick: BuyDockTierPick | null;
}

export interface Command<T extends CommandType = CommandType> {
    ns: typeof PROTOCOL_NAMESPACE;
    v: typeof PROTOCOL_VERSION;
    id: string;
    type: T;
    payload: CommandPayloads[T];
}

export interface ReplyOk<D = unknown> {
    ns: typeof PROTOCOL_NAMESPACE;
    v: typeof PROTOCOL_VERSION;
    id: string;
    ok: true;
    data: D;
}

export interface ReplyError {
    ns: typeof PROTOCOL_NAMESPACE;
    v: typeof PROTOCOL_VERSION;
    id: string;
    ok: false;
    error: string;
}

export type Reply<D = unknown> = ReplyOk<D> | ReplyError;

/**
 * The reverse channel: the cart PANEL asking the HOST page to do something.
 *
 * Everything else in this file flows host → bridge. On a Shopify storefront
 * the cart lives on the shop's own origin and only the host page can reach
 * its AJAX API (`/cart.js` is same-origin), so the drawer — which renders
 * inside our iframe — asks the SDK to read and change it. The SDK answers
 * with an ordinary `Reply` carrying the same `id`.
 *
 * Distinguished from a `Command` by the `host` field (a Command has `type`),
 * so neither side can mistake one for the other.
 */
export type HostCommandType =
    | "host.cart.get"
    | "host.cart.add"
    | "host.cart.change"
    | "host.cart.attributes"
    | "host.cart.flush"
    | "host.checkout"
    /** A buy dock frame's actions, served by the SDK's buy dock on any platform. */
    | "host.buydock.add"
    | "host.buydock.quantity"
    | "host.buydock.buy"
    | "host.buydock.cart"
    | "host.buydock.delivery"
    | "host.buydock.tier"
    /** A bundle box frame's actions, served by the SDK's box module. */
    | "host.box.add"
    | "host.box.cart"
    | "host.box.reveal";

export interface HostCommandPayloads {
    "host.cart.get": Record<string, never>;
    "host.cart.add": { id: number; quantity: number; properties?: Record<string, unknown> };
    /** `id` is a line key or a variant id; quantity 0 removes the line. */
    "host.cart.change": { id: string | number; quantity: number };
    "host.cart.attributes": { attributes: Record<string, string> };
    /** Wait for queued cart mirrors to reach the store. */
    "host.cart.flush": Record<string, never>;
    /** `discount_code` is handed to Shopify's own discount route;
     *  `checkout_provider` is the cart designer's Checkout choice
     *  ("auto" | "shopify" | a checkout app id such as "gokwik"). */
    "host.checkout": { discount_code?: string; checkout_provider?: string };
    /** `with`: bundle companions (store product ids), added beside the variant on screen. */
    "host.buydock.add": { quantity: number; with?: Array<string | number> };
    /** The variant on screen's line; 0 removes it. */
    "host.buydock.quantity": { quantity: number };
    "host.buydock.buy": { quantity: number };
    "host.buydock.cart": Record<string, never>;
    /** Answers with a `DeliveryEstimate`, or null when the store could not say. */
    "host.buydock.delivery": { pincode: string };
    "host.buydock.tier": { pick: BuyDockTierPick | null };
    /**
     * Put a filled box in the shop's cart: `variant_id` is the hidden bundle the
     * store made for it. `fresh` means it was made just now, which a shop's cart
     * can take a moment to see. Answers `{ opened }` (whether the drawer opened),
     * or fails with "BUNDLE_GONE" when Shopify no longer knows the variant.
     * `hold` leaves the drawer shut and answers `{ opened: false, open }` (whether
     * it would have opened); the picker opens it once its beat has played.
     */
    "host.box.add": { variant_id: string; quantity: number; fresh?: boolean; hold?: boolean };
    /** Open the cart: the drawer, or the theme's cart page without one. */
    "host.box.cart": Record<string, never>;
    /** Bring the tray into view (the rail beside the picker). */
    "host.box.reveal": Record<string, never>;
}

export interface HostCommand<T extends HostCommandType = HostCommandType> {
    ns: typeof PROTOCOL_NAMESPACE;
    v: typeof PROTOCOL_VERSION;
    id: string;
    host: T;
    payload: HostCommandPayloads[T];
}

export function isHostCommand(data: unknown): data is HostCommand {
    return isBridgeMessage(data) && typeof (data as HostCommand).host === "string";
}

/**
 * Events pushed without being asked.
 *
 * `cart:*` come from either the bridge or the cart panel — both run on our
 * origin, so both share the shopper's token and either can be the one that
 * changed the cart. `panel:close` is the drawer asking the SDK to dismiss it,
 * since only the host page can hide the overlay it created.
 */
export type EventName =
    | "cart:updated"
    /** A coupon's fate changed: applied, rejected, or removed. */
    | "cart:coupon"
    /** A line changed by shopper action: add, remove, qty, variant, gift. */
    | "cart:item"
    /** Checkout tapped — everything a host needs to run ANY checkout,
     *  including a third-party one, without reading our iframe. */
    | "cart:checkout"
    /**
     * The guest cart's token was minted, replaced or cleared (token-cart
     * stores). The SDK persists it on the host page — first-party storage is
     * the only kind that reliably survives on a third-party embed — and hands
     * it back at the next `bridge.init`.
     */
    | "cart:tokenchanged"
    | "bridge:ready"
    | "bridge:error"
    /**
     * The chat frame's own footprint, in CSS pixels, and whether its panel is
     * open.
     *
     * The chat is not a drawer: it is a launcher that grows into a docked
     * popup, so the frame has to be exactly as big as whichever of the two is
     * showing — any larger and a transparent rectangle would eat clicks meant
     * for the shop. Only the widget knows that size, and only the host can
     * apply it, so the widget reports it on every change.
     */
    | "chat:frame"
    /** A buy dock frame loaded; the SDK answers with `buydock.state`. */
    | "buydock:ready"
    /**
     * A buy dock frame's footprint in CSS pixels: the inline dock's content
     * height and where its action row sits, or how much of the screen's bottom
     * a sticky bar needs (popovers and tips included).
     */
    | "buydock:frame"
    /** Buy Now on a page that runs its own checkout: { product_id, variant_id, quantity }. */
    | "buydock:buynow"
    /** The dock's load finished: { ok: true, product_id, variant_id, platform } as the store resolved them, or { ok: false, message }. */
    | "buydock:loaded"
    /**
     * Bundle box frames. The picker frame owns the box: it sends the box once
     * (`box:init`) and its state on every change (`box:sync`); the page relays
     * both to the rail, strip and sheet frames, which send the shopper's taps
     * back as `box:action`. Each frame reports its footprint (`box:frame`), the
     * sheet frame its mode (`box:overlay`). `box:loaded` tells the page whether
     * a box showed at all. The picker says where its rail column is (`box:slot`,
     * null when there is none) so the page can lay the rail frame over it.
     */
    | "box:ready"
    | "box:init"
    | "box:sync"
    | "box:action"
    | "box:frame"
    | "box:overlay"
    | "box:loaded"
    | "box:slot"
    /**
     * The account page (account.html), mounted inline by `accountPage.mount()`.
     * `account:frame` is its content height in CSS pixels, so the host sizes the
     * frame and the page scrolls with the site instead of inside a box.
     * `account:login` asks the host to open the sign-in panel. `account:navigate`
     * asks it to open an order on the hosted order page, `{ url }`, in the same
     * tab (the SDK checks the host). `account:route` says which section is on
     * screen, `{ route }`, for a host that keeps it in its own URL.
     *
     * The desktop side panel is a frame of its own (`surface=panel`) that the
     * host keeps sticky beside the account, as the bundle box's rail.
     * `account:slot` is where its column is in the account frame (null: no
     * panel); `account:panel` what it shows, relayed to the panel frame, which
     * says `account:ready` when it can draw it. Its taps come back through the
     * host: `account:go` `{ key }` opens a section, `account:logout` asks to
     * log out.
     */
    | "account:frame"
    | "account:login"
    | "account:navigate"
    | "account:route"
    | "account:slot"
    | "account:panel"
    | "account:ready"
    | "account:go"
    | "account:logout"
    | "panel:close"
    | "panel:checkout"
    | "panel:signedin"
    /**
     * How big the panel's own card is, in CSS pixels — and how its corners are
     * cut.
     *
     * A drawer does not need the SIZE — it fills a fixed edge of the screen. A
     * MODAL does: the card is centred with a dimmed page behind it, so the
     * frame has to be exactly the card and nothing more, or the transparent
     * margin around it swallows clicks meant for the backdrop.
     *
     * Both need the RADIUS. The host's frame is a square box; the card inside
     * it is rounded. Anything the host paints on that box — a drawer's white
     * surface, a future backdrop or ring — therefore shows through the corner
     * cut as a hard square edge around a soft card. The card is the only side
     * that knows its own radius, and it is a merchant-facing design token, so
     * it travels with the size rather than being duplicated as a constant in
     * a bundle that ships and caches separately from this one.
     *
     * Reported by the panel because only it can measure content it renders,
     * and re-reported whenever that content changes height — the sign-in card
     * is as tall as its current step.
     */
    | "panel:size"
    /**
     * The shopper asked for one of the store's legal pages.
     *
     * The panel cannot open it itself. It is a cross-origin iframe, so
     * `window.top.location` is not its to set — and it should not be: the
     * merchant's page may have a cart mid-edit or a form part-filled, and a
     * frame quietly navigating the tab out from under it is exactly the
     * behaviour iframes are sandboxed to prevent. So it asks, and the host —
     * which owns the tab — closes the panel and navigates.
     *
     * Payload `{ url }`. The host validates before following it: this arrives
     * from a merchant-authored config, and nothing else on the way has a
     * reason to check the scheme.
     */
    | "panel:navigate"
    /**
     * The shopper's signed-in state, reported on EVERY page load and again on
     * any change. Emitted when they were signed in silently, when they were
     * already signed in from a previous visit, and when they sign out
     * (`signed_in: false`).
     *
     * It fires for the unchanged case on purpose: the host page starts each
     * load knowing nothing, so a state that is never announced renders a
     * signed-in shopper as a guest.
     *
     * Carries `auth_token` — this store's bearer for this shopper — so a host
     * page can adopt the session rather than run its own handoff. It is the
     * same token that store's own API would have issued, sent only to an
     * origin the tenant has registered, so this is not a wider disclosure than
     * the host already had. A host with its own session layer can ignore it.
     *
     * Not on a Shopify storefront (EmbedConfig `share_session_with_host`
     * false): every app script there can read it, so the token and the
     * shopper's mobile are left out of this and `panel:signedin`.
     */
    | "auth:changed"
    /**
     * The shopper is recognised from ANOTHER store, so we did NOT sign them
     * in: doing so silently would hand this merchant their details for a shop
     * they have never used. Payload carries just enough to offer it
     * ("Continue as Haard?"); the host decides whether to show it.
     */
    | "auth:available";

export interface EventMessage<P = unknown> {
    ns: typeof PROTOCOL_NAMESPACE;
    v: typeof PROTOCOL_VERSION;
    event: EventName;
    payload: P;
}

export type BridgeMessage = Command | Reply | EventMessage | HostCommand;

/**
 * Type guard used on BOTH sides before touching any message. `event.data` is
 * attacker-controlled — other scripts on the merchant's page post messages
 * too — so shape-checking is not optional.
 */
export function isBridgeMessage(data: unknown): data is BridgeMessage {
    if (!data || typeof data !== "object") return false;
    const m = data as Partial<BridgeMessage>;
    return m.ns === PROTOCOL_NAMESPACE && m.v === PROTOCOL_VERSION;
}

export function isCommand(data: unknown): data is Command {
    return isBridgeMessage(data) && typeof (data as Command).type === "string";
}

export function isReply(data: unknown): data is Reply {
    return isBridgeMessage(data) && typeof (data as Reply).ok === "boolean";
}

export function isEventMessage(data: unknown): data is EventMessage {
    return isBridgeMessage(data) && typeof (data as EventMessage).event === "string";
}
